import PhotoSwipeLightbox from 'photoswipe/lightbox';
import type PhotoSwipe from 'photoswipe';
import 'photoswipe/style.css';

// Product photo zoom (PhotoSwipe), set up like phoebephilo.com:
// - a photo opens from where it is on the page and fills the screen ("fill")
// - the first click / tap switches to the whole photo ("fit": full height or full width),
//   and from then on every photo shows whole, also after ← → (until the viewer is closed)
// - in that mode, click / tap zooms in where you clicked, and again back to the whole photo
// - drag to look around, pinch to zoom on phones, drag down, CLOSE or Esc to close
// - ← → between photos (loops), CLOSE top right
// - product videos open big, playing (muted, looping)
// Photos and videos come from the data-zoom-* attributes in snippets/page-product.liquid,
// in the same order as on the page (so indexes match the mobile slider).
const photos = Array.from(
  document.querySelectorAll('.m-item__image[data-zoom-src], .m-item__image[data-zoom-video]')
) as HTMLElement[];

type ZoomLevels = { fit: number; fill: number };

// true once the whole-photo mode was chosen; reset each time the viewer opens
let wholePhotoMode = false;

const zoomedIn = (levels: ZoomLevels) => Math.max(levels.fill, levels.fit * 2);

function onPhotoClick(this: PhotoSwipe, point: { x: number; y: number }) {
  const slide = this.currSlide;
  if (!slide || !slide.isZoomable()) return;

  const duration = this.options.zoomAnimationDuration;

  if (!wholePhotoMode) {
    wholePhotoMode = true;
    // the photos either side switch to the whole-photo view too
    this.mainScroll.itemHolders.forEach(({ slide: other }) => {
      if (other && other !== slide) other.resize();
    });
    slide.calculateSize();
    slide.zoomTo(slide.zoomLevels.fit, point, duration);
    return;
  }

  const levels = slide.zoomLevels;
  const isZoomedIn = slide.currZoomLevel > levels.fit + 0.001;
  slide.zoomTo(isZoomedIn ? levels.fit : zoomedIn(levels), point, duration);
}

export const zoomLightbox = photos.length
  ? new PhotoSwipeLightbox({
      dataSource: photos.map((photo) =>
        photo.dataset.zoomVideo
          ? {
              html: `<video class="m-zoom-video" src="${photo.dataset.zoomVideo}" autoplay muted loop playsinline></video>`,
              element: photo,
            }
          : {
              src: photo.dataset.zoomSrc,
              width: Number(photo.dataset.zoomWidth),
              height: Number(photo.dataset.zoomHeight),
              element: photo,
              thumbCropped: true,
            }
      ),
      pswpModule: () => import('photoswipe'),
      loop: true,
      showAnimationDuration: 500,
      initialZoomLevel: (levels) => (wholePhotoMode ? levels.fit : levels.fill),
      secondaryZoomLevel: (levels) => (wholePhotoMode ? zoomedIn(levels) : levels.fit),
      bgOpacity: 1,
      zoom: false,
      close: false,
      counter: false,
      arrowPrevSVG: '←',
      arrowNextSVG: '→',
      closeOnVerticalDrag: true,
      tapAction: onPhotoClick,
      doubleTapAction: onPhotoClick,
      imageClickAction: onPhotoClick,
      bgClickAction: onPhotoClick,
      preloadFirstSlide: true,
    })
  : null;

if (zoomLightbox) {
  // show the photo already on the page while the big one loads
  zoomLightbox.addFilter('itemData', (itemData) => {
    const img = (itemData.element as HTMLElement | undefined)?.querySelector('img');
    return { ...itemData, msrc: img?.currentSrc || img?.src };
  });

  // CLOSE in our heading font instead of PhotoSwipe's × icon
  zoomLightbox.on('uiRegister', () => {
    zoomLightbox.pswp?.ui?.registerElement({
      name: 'paracia-close',
      className: 'm-zoom-close',
      ariaLabel: 'Close',
      order: 20,
      isButton: true,
      html: 'CLOSE',
      onClick: (_event, _el, pswp) => pswp.close(),
    });
  });

  // hide the page's scrollbar while open (it sat right next to the → arrow),
  // padding the page by its width so nothing behind the viewer shifts
  const lockPage = () => {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.classList.add('zoom-is-open');
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
  };

  const unlockPage = () => {
    document.documentElement.classList.remove('zoom-is-open');
    document.body.style.paddingRight = '';
  };

  zoomLightbox.on('beforeOpen', () => {
    wholePhotoMode = false;
    lockPage();
  });
  zoomLightbox.on('destroy', unlockPage);

  // mouse cursor, set from the mouse position (Safari ignores a cursor set on the photo itself):
  // magnifier over the photo on the whole-photo view (a click zooms in), a hand over the photo
  // when zoomed in (drag to move it), the normal cursor elsewhere (black background, arrows, CLOSE)
  let lastPointer: { x: number; y: number } | null = null;
  let dragging = false;

  // Safari on the Mac doesn't draw the standard magnifier (cursor: zoom-in) at all,
  // so Safari gets a small picture of it instead (18px; 1x + 2x for Retina screens)
  const isSafari = /^((?!chrome|chromium|crios|fxios|edg|android).)*safari/i.test(navigator.userAgent);
  const magnifier1x = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABIAAAASCAYAAABWzo5XAAACj0lEQVR42p2TP0hbURSHz8sfFQ0Smk0QIRQMaLHiopkiGazkQZ6OpdBNQaQUFNosOlZ4nWpKN6WCmyg4u6WLQrEdfLhYqOYtGqOxSm3eu+/rksirxhb6Ww4/7j3fPZxzrohPQNDvg8E/7J1zvzTfpYCmaR7QLSLPRWTo+vr6QUtLy7mIbIvIR03T9ur3GtLqLwEvj4+Pf+ZyOfr7+4nH4/T19TE7O4tt21Xg9b2V+SAvDg4OiMViJJNJZ3l52d3a2lIrKyvu8PCw097e7lmWxb9gDy8uLqqtra3u3NycooFM01ShUMg5OTnxgEf1dtwGLeRyOQYGBpx6YqVSQdd1Dg8Pb2CpVMqZmpoC+FDLC90Gferp6fFWV1ddpRRnZ2dYlkUwGKRQKFAul1FKsbm5qeLxuAd8BTT/sATQrq6u9rq6uigUCgpA13Wi0SiRSIRoNEo6nQZgd3dXdXR0UCqVvgNN9XwRkZCmaQDnbW1t2LaN53mSz+elWCxKKpWSjY0N6e3tFc/zxLZtaW5ullgs9kNEHBHRNE3DX9XbmZkZksnkTY8uLy9Jp9MUi8WbHo2OjjoTExMAi0CgUY8Sp6enKhwOu6ZpNpza0tISIuLWwE9reU2N9uiVZVlEIhE1MjLirK2tqe3tbbW+vq4ymYwjIq6IKMMwFHACDN2ZnA+WOzo6cqanp0kkEnR2dtLd3c3k5CT7+/vouo6IqLGxMYCKDxb2wwK1+BjIA5/L5fI34AvwHnjmum7FMAxEpJrNZuuwwXu/i8833fJDwHk2m0VEqoZhUK1WS0CmEezONICgb2+GgIvx8XFE5NfCwgLAovxN9WXz+VAtDrquWzJNk52dnTfyP/LBngDvRETm5+cDvwHLpEXcTkSaMAAAAABJRU5ErkJggg==';
  const magnifier2x = 'data:image/png;base64,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';
  const safariZoomCursors = [
    `-webkit-image-set(url("${magnifier1x}") 1x, url("${magnifier2x}") 2x) 8 8, zoom-in`,
    `url("${magnifier1x}") 8 8, zoom-in`,
  ];

  const setCursor = (wrap: HTMLElement, cursor: string) => {
    if (cursor !== 'zoom-in' || !isSafari) {
      wrap.style.cursor = cursor;
      return;
    }
    // use the first form this Safari accepts
    for (const value of safariZoomCursors) {
      wrap.style.cursor = value;
      if (wrap.style.cursor) return;
    }
    wrap.style.cursor = cursor;
  };

  const updateCursor = () => {
    const pswp = zoomLightbox.pswp;
    const wrap = pswp?.scrollWrap;
    const slide = pswp?.currSlide;
    if (!wrap) return;

    let cursor = '';
    const photo = slide?.container.querySelector('.pswp__img:last-of-type');
    if (lastPointer && photo && slide?.isZoomable()) {
      const box = photo.getBoundingClientRect();
      const overPhoto =
        lastPointer.x >= box.left && lastPointer.x <= box.right &&
        lastPointer.y >= box.top && lastPointer.y <= box.bottom;
      if (overPhoto) cursor = slide.isPannable() ? (dragging ? 'grabbing' : 'grab') : 'zoom-in';
    }
    setCursor(wrap, cursor);
  };

  zoomLightbox.on('bindEvents', () => {
    const wrap = zoomLightbox.pswp?.scrollWrap;
    if (!wrap) return;
    wrap.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'mouse') return;
      lastPointer = { x: event.clientX, y: event.clientY };
      updateCursor();
    });
    wrap.addEventListener('pointerdown', () => {
      dragging = true;
      updateCursor();
    });
  });
  window.addEventListener('pointerup', () => {
    if (!dragging) return;
    dragging = false;
    updateCursor();
  });
  zoomLightbox.on('zoomPanUpdate', updateCursor);
  zoomLightbox.on('change', updateCursor);
  zoomLightbox.on('close', () => {
    lastPointer = null;
  });

  // trackpad: a two-finger sideways swipe goes to the previous / next photo
  // when the whole photo is showing (zoomed in, two fingers move the photo instead).
  // After a swipe, the Mac keeps sending "momentum" events for up to a second;
  // those are ignored, but a new swipe (a pause, or the fingers speeding up
  // again while the momentum fades) is picked up straight away.
  let swipeTotal = 0;
  let swipeLocked = false;
  let lastWheelAt = 0;
  let lastWheelDelta = 0;
  let lastSwipeAt = 0;

  zoomLightbox.on('wheel', (event) => {
    const pswp = zoomLightbox.pswp;
    const wheel = event.originalEvent;
    const slide = pswp?.currSlide;
    if (!pswp || !slide || wheel.ctrlKey) return;

    // zoomed in (filled or closer): two fingers move the photo, as PhotoSwipe does by default
    const sideways = Math.abs(wheel.deltaX) > Math.abs(wheel.deltaY);
    if (!sideways || slide.isPannable()) return;

    event.preventDefault();

    const now = performance.now();
    const delta = Math.abs(wheel.deltaX);
    const pause = now - lastWheelAt;

    if (swipeLocked) {
      const speedingUp = now - lastSwipeAt > 200 && delta > 6 && delta > lastWheelDelta * 1.5;
      if (pause > 100 || speedingUp) {
        swipeLocked = false;
        swipeTotal = 0;
      }
    } else if (pause > 100) {
      swipeTotal = 0;
    }

    lastWheelAt = now;
    lastWheelDelta = delta;
    if (swipeLocked) return;

    swipeTotal += wheel.deltaX;
    if (Math.abs(swipeTotal) > 25) {
      swipeLocked = true;
      lastSwipeAt = now;
      if (swipeTotal > 0) pswp.next();
      else pswp.prev();
    }
  });

  zoomLightbox.init();
}

export const openZoom = (index: number): void => {
  zoomLightbox?.loadAndOpen(index);
};
